import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { shareUrl, mediaSrc } from "../lib/media";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Copy, Check, Eye, Trash2, Link2, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

const EXPIRY = [[24, "24 hours"], [72, "3 days"], [168, "7 days"], [720, "30 days"]];

const timeLeft = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h || 1}h left`;
  return `${Math.round(h / 24)}d left`;
};

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success(tr("Link copied")); }
  catch (e) { toast.error(tr("Couldn't copy — select the link and copy it manually")); }
};

const Pick = ({ item, on, toggle, idx }) => (
  <button type="button" data-testid={`share-pick-${idx}`} onClick={toggle}
    className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${on ? "border-brand-green" : "border-transparent opacity-50 hover:opacity-80"}`}>
    {item.kind === "video" ? (
      item.poster ? <img src={mediaSrc(item.poster)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center bg-black/60"><Play className="h-4 w-4 text-white" /></div>
    ) : <img src={mediaSrc(item.url)} alt="" className="h-full w-full object-cover" />}
    {item.kind === "video" && <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[9px] font-semibold uppercase text-white">{tr("Video")}</span>}
    {on && <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-brand-green text-white"><Check className="h-3 w-3" /></span>}
  </button>
);

export const ShareDialog = ({ property, items, files, onClose }) => {
  const videoUrls = items.filter((i) => i.kind === "video").map((i) => i.url);
  const [selected, setSelected] = useState(() => new Set(videoUrls.length ? videoUrls : items.map((i) => i.url)));
  const [hours, setHours] = useState(168);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [shares, setShares] = useState([]);
  const [copied, setCopied] = useState(false);

  const loadShares = useCallback(async () => {
    try { const r = await client.get(`/properties/${property.id}/shares`); setShares(r.data); } catch (e) { console.error("shares load failed", e); }
  }, [property.id]);
  useEffect(() => { loadShares(); }, [loadShares]);

  const toggle = (url) => setSelected((s) => { const n = new Set(s); if (n.has(url)) n.delete(url); else n.add(url); return n; });
  const all = [...items, ...files.map((f) => ({ ...f, kind: "file" }))];

  const create = async () => {
    if (!selected.size) { toast.error(tr("Pick at least one video or photo")); return; }
    setCreating(true);
    try {
      const r = await client.post(`/properties/${property.id}/share`, { urls: [...selected], expires_hours: hours, label });
      setCreated(r.data); loadShares();
      await copy(shareUrl(r.data.token)); setCopied(true);
    } catch (e) { toast.error(tr("Couldn't create link"), { description: e?.response?.data?.detail }); }
    finally { setCreating(false); }
  };

  const revoke = async (token) => {
    try { await client.post(`/properties/${property.id}/shares/${token}/revoke`); toast.success(tr("Link switched off")); loadShares(); if (created?.token === token) setCreated(null); }
    catch (e) { toast.error(tr("Couldn't revoke")); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="share-dialog" className="glass-strong max-w-2xl border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-white"><Link2 className="h-5 w-5 text-brand-green" /> {tr("Share a walkthrough")}</DialogTitle>
          <DialogDescription>{tr("Send agents or investors a private, time-limited link to this property's videos and photos. No login needed on their side; the link dies on its own.")}</DialogDescription>
        </DialogHeader>

        {created ? (
          <div data-testid="share-created" className="rounded-xl border border-brand-green/30 bg-brand-green/[0.06] p-4">
            <p className="text-xs uppercase tracking-widest text-brand-green">Link ready · {timeLeft(created.expires_at)}</p>
            <div className="mt-2 flex items-center gap-2">
              <Input readOnly value={shareUrl(created.token)} data-testid="share-link-input" onFocus={(e) => e.target.select()} className="h-11 bg-black/30 border-white/10 font-mono text-xs text-white" />
              <Button onClick={() => { copy(shareUrl(created.token)); setCopied(true); }} data-testid="share-copy-btn" className="h-11 bg-brand-green text-white hover:bg-brand-green/90">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy</Button>
            </div>
            <div className="mt-3 flex gap-2">
              <a href={shareUrl(created.token)} target="_blank" rel="noreferrer" data-testid="share-preview-link" className="text-xs text-muted-foreground underline-offset-2 hover:text-white hover:underline">{tr("Preview what they'll see")}</a>
              <button onClick={() => { setCreated(null); setCopied(false); }} className="text-xs text-muted-foreground hover:text-white">· Make another</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">What to include · {selected.size} selected</p>
                <div className="flex gap-2 text-xs">
                  <button type="button" data-testid="share-pick-videos" onClick={() => setSelected(new Set(videoUrls))} className="text-muted-foreground hover:text-white">{tr("Videos only")}</button>
                  <button type="button" data-testid="share-pick-all" onClick={() => setSelected(new Set(all.map((i) => i.url)))} className="text-muted-foreground hover:text-white">{tr("Everything")}</button>
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {items.map((it, i) => <Pick key={it.url} idx={i} item={it} on={selected.has(it.url)} toggle={() => toggle(it.url)} />)}
                {files.map((f, i) => (
                  <button key={f.url} type="button" data-testid={`share-pick-file-${i}`} onClick={() => toggle(f.url)}
                    className={`h-16 w-24 shrink-0 rounded-lg border-2 px-2 text-left text-[10px] leading-tight text-white transition-colors ${selected.has(f.url) ? "border-brand-green bg-white/[0.04]" : "border-white/10 opacity-50 hover:opacity-80"}`}>
                    <span className="line-clamp-3">{f.name}</span>
                  </button>
                ))}
              </div>
              {!items.length && !files.length && <p className="text-sm text-muted-foreground">{tr("Upload a video or photo first.")}</p>}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">{tr("Link expires after")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {EXPIRY.map(([h, l]) => (
                    <button key={h} type="button" data-testid={`share-expiry-${h}`} onClick={() => setHours(h)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${hours === h ? "border-brand-green/40 bg-brand-green/15 text-brand-green" : "border-white/12 text-muted-foreground hover:text-white"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">{tr("Who is it for (optional)")}</p>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={tr("e.g. Youssef — agent, Gueliz")} data-testid="share-label-input" className="h-10 bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              </div>
            </div>
            <Button onClick={create} disabled={creating || !selected.size} data-testid="share-create-btn" className="h-11 w-full bg-brand-green text-white hover:bg-brand-green/90">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Create link &amp; copy
            </Button>
          </div>
        )}

        {shares.length > 0 && (
          <div data-testid="share-active-list" className="border-t border-white/[0.06] pt-4">
            <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">{tr("Active links")}</p>
            <div className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
              {shares.map((s, i) => (
                <div key={s.token} data-testid={`share-row-${i}`} className={`flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 ${s.expired ? "opacity-50" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">{s.label || `${s.urls.length} item${s.urls.length === 1 ? "" : "s"}`}</p>
                    <p className="text-[11px] text-muted-foreground">{s.created_by_name || "Staff"} · {timeLeft(s.expires_at)}</p>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground" title={tr("Views")}><Eye className="h-3.5 w-3.5" /> {s.views || 0}</span>
                  <button onClick={() => copy(shareUrl(s.token))} data-testid={`share-row-copy-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-white/5 hover:text-white"><Copy className="h-4 w-4" /></button>
                  <button onClick={() => revoke(s.token)} data-testid={`share-row-revoke-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-brand-red/15 hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
